import type { LucideIcon } from 'lucide-react';

export interface Aba {
  id: string;
  rotulo: string;
  icone?: LucideIcon;
  /** Contagem ao lado do rótulo — "Itens (3)". Zero não aparece. */
  contagem?: number;
  /** Aba que ainda não pode ser aberta (falta preencher a anterior). */
  bloqueada?: boolean;
}

/**
 * Abas.
 *
 * Uma tela só, com recortes — e não telas separadas. É o padrão que o sistema
 * de referência usa na consulta e no lançamento de pedido, e a razão é a mesma
 * nos dois: quem está lançando um pedido precisa ir e voltar entre o cliente,
 * os itens e o pagamento antes de gravar, e quem está consultando precisa ver
 * a mesma leva pelo resumo, pelo produto e pelo representante sem refazer o
 * filtro.
 *
 * **Rola na horizontal em vez de quebrar.** Com oito abas numa tela de celular,
 * quebrar em duas linhas empurra o conteúdo para baixo da dobra; rolar mantém a
 * primeira aba visível, que é a que a pessoa mais usa.
 *
 * A aba BLOQUEADA fica cinza e não recebe clique. É o que substitui o
 * "próximo/anterior" de um assistente: em vez de esconder as etapas seguintes,
 * mostra-as desde o começo — quem lança um pedido quer saber, de saída, quantos
 * passos faltam.
 */
export function Abas({
  abas,
  ativa,
  aoEscolher,
}: {
  abas: readonly Aba[];
  ativa: string;
  aoEscolher: (id: string) => void;
}) {
  return (
    /* `px-1` fica: o `:focus-visible` global pinta `outline-offset-2` = 4px
       para fora e `overflow-x-auto` recorta no padding edge — sem ele o anel
       da primeira e da última aba some. O `-mx-1` é que não podia existir:
       alargava a caixa em 8px e o `border-b` escapava 4px além da barra de
       filtros e da tabela (e vazava da coluna em `sociedade.tsx:240`). */
    <div
      role="tablist"
      aria-label="Seções da tela"
      className="border-tinta-100 flex gap-1 overflow-x-auto border-b px-1"
    >
      {abas.map((aba) => {
        const selecionada = aba.id === ativa;
        const Icone = aba.icone;
        return (
          /* Sem `-mb-px`: a margem negativa fazia a caixa de borda do botão
             (38px) terminar 1px abaixo do content box do tablist (37px), e
             `overflow-x: auto` com `overflow-y: visible` força o eixo Y a
             computar `auto` (CSS Overflow 3) — nascia barra de rolagem
             vertical sem nada para rolar. No isotec (tabs.tsx:100) não há
             margem negativa nenhuma: os 2px da aba ativa simplesmente
             empilham sobre o 1px do tablist. `px-4 py-2.5` é o alvo de
             clique do isotec. */
          <button
            key={aba.id}
            type="button"
            role="tab"
            aria-selected={selecionada}
            disabled={aba.bloqueada === true}
            onClick={() => aoEscolher(aba.id)}
            className={`flex shrink-0 items-center gap-2 rounded-t-md border-b-2 px-4 py-2.5 text-sm whitespace-nowrap transition-colors ${
              selecionada
                ? 'border-marca-600 text-marca-600 font-medium'
                : aba.bloqueada
                  ? 'text-tinta-300 border-transparent'
                  : 'text-tinta-500 hover:text-tinta-900 hover:border-tinta-300 border-transparent'
            }`}
          >
            {Icone && <Icone className="size-4 shrink-0" aria-hidden />}
            {aba.rotulo}
            {aba.contagem !== undefined && aba.contagem > 0 && (
              <span
                className={`rounded-full px-1.5 text-xs tabular-nums ${
                  selecionada
                    ? 'bg-marca-100 text-marca-600'
                    : 'bg-tinta-100 text-tinta-500'
                }`}
              >
                {aba.contagem}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
